import React, { useCallback, useMemo } from "react";
import { Container } from "reactstrap";
import { COLOR_SECONDARY } from "../../../constants/theme";
import { useAnalyticsCore } from "../../../cores/analytics";
import { ChartMetric } from "../../../cores/analytics/interface/ChartMetric";
import { Metric2 } from "../../../cores/analytics/interface/Metric2";
import { CL$Format } from "../../../cores/utils";
import { useWooCommerceCore } from "../../../cores/wooCommerce";
import { ChartContainer } from "./ChartContainer";
import { BarChart } from "./charts/BarChart";
import { Monitors } from "./Monitors";
import { useIntl } from "react-intl";


export const WooCommerceCharts = () => {
  const intl = useIntl();
  const { wooCommerceInsights, toChartMetric } = useAnalyticsCore();
  const { currency } = useWooCommerceCore();

  const ordersCurrencyData: Metric2[] = useMemo(
    () => [
      {
        ...wooCommerceInsights.woo_sales,
        type: "line",
        valueFormatter: (value: number) => CL$Format(value, currency?.code),
      },
      {
        ...wooCommerceInsights.woo_taxes,
        type: "line",
        valueFormatter: (value: number) => CL$Format(value, currency?.code),
      },
      {
        ...wooCommerceInsights.woo_shipping,
        type: "line",
        valueFormatter: (value: number) => CL$Format(value, currency?.code),
      },
      {
        ...wooCommerceInsights.woo_discount,
        type: "line",
        valueFormatter: (value: number) => CL$Format(value, currency?.code),
      },
      {
        ...wooCommerceInsights.woo_orders,
        type: "bar",
        color: COLOR_SECONDARY,
      },
    ],
    [currency, wooCommerceInsights]
  );

  const ordersItemsData: Metric2[] = useMemo(
    () => [
      {
        ...wooCommerceInsights.woo_items,
        type: "line",
      },
      {
        ...wooCommerceInsights.woo_customers,
        type: "line",
      },
      {
        ...wooCommerceInsights.woo_orders,
        type: "bar",
        color: COLOR_SECONDARY,
      },
    ],
    [wooCommerceInsights]
  );

  const ordersChartMetrics: ChartMetric = useMemo(
    () => toChartMetric(ordersCurrencyData),
    [ordersCurrencyData, toChartMetric]
  );

  const ordersMonitor = useCallback(
    () => <Monitors metrics={ordersCurrencyData} />,
    [ordersCurrencyData]
  );

  const ordersItemChartMetrics: ChartMetric = useMemo(
    () => toChartMetric(ordersItemsData),
    [ordersItemsData, toChartMetric]
  );

  const ordersItemMonitor = useCallback(
    () => <Monitors metrics={ordersItemsData} />,
    [ordersItemsData]
  );

  return (
    <Container>
      <ChartContainer
        id="wooCommerceChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.metricsWocommerce.title1" })}
        monitors={ordersMonitor()}
      >
        <BarChart
          labels={ordersChartMetrics.labels}
          datasets={ordersChartMetrics.datasets}
        />
      </ChartContainer>
      <ChartContainer
        id="wooCommerceChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.metricsWocommerce.title2" })}
        monitors={ordersItemMonitor()}
      >
        <BarChart
          labels={ordersItemChartMetrics.labels}
          datasets={ordersItemChartMetrics.datasets}
        />
      </ChartContainer>
    </Container>
  );
};
